import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  items: [],
  isLoading: false,
  error: null
};

// Load watchlist from localStorage on initialization
const loadWatchlistFromStorage = () => {
  try {
    const savedWatchlist = localStorage.getItem('watchlist');
    return savedWatchlist ? JSON.parse(savedWatchlist) : [];
  } catch (error) {
    console.error('Error loading watchlist from localStorage:', error);
    return [];
  }
};

// Save watchlist to localStorage
const saveWatchlistToStorage = (items) => {
  try {
    localStorage.setItem('watchlist', JSON.stringify(items));
  } catch (error) {
    console.error('Error saving watchlist to localStorage:', error);
  }
};

const watchlistSlice = createSlice({
  name: 'watchlist',
  initialState: {
    ...initialState,
    items: loadWatchlistFromStorage()
  },
  reducers: {
    addToWatchlist: (state, action) => {
      const product = action.payload;
      const existingItem = state.items.find(item => item.id === product.id);
      
      if (!existingItem) {
        const watchlistItem = {
          id: product.id,
          name: product.name,
          sku: product.sku,
          price: product.price,
          list_price: product.list_price,
          image_url: product.image_url,
          make: typeof product.make === 'object' ? product.make?.name : product.make,
          model: typeof product.model === 'object' ? product.model?.name : product.model,
          year: product.year,
          body_type: typeof product.body_type === 'object' ? product.body_type?.name : product.body_type,
          position: typeof product.position === 'object' ? product.position?.name : product.position,
          addedAt: new Date().toISOString(),
          slug: product.slug,
          uuid: product.uuid,
          product_uuid: product.product_uuid
        };
        
        state.items.push(watchlistItem);
        saveWatchlistToStorage(state.items);
      }
    },
    
    removeFromWatchlist: (state, action) => {
      const productId = action.payload;
      state.items = state.items.filter(item => item.id !== productId);
      saveWatchlistToStorage(state.items);
    },
    
    clearWatchlist: (state) => {
      state.items = [];
      saveWatchlistToStorage(state.items);
    },
    
    toggleWatchlistItem: (state, action) => {
      const product = action.payload;
      const existingItemIndex = state.items.findIndex(item => item.id === product.id);
      
      if (existingItemIndex >= 0) {
        // Remove from watchlist
        state.items.splice(existingItemIndex, 1);
      } else {
        // Add to watchlist
        const watchlistItem = {
          id: product.id,
          name: product.name,
          sku: product.sku,
          price: product.price,
          list_price: product.list_price,
          image_url: product.image_url,
          make: typeof product.make === 'object' ? product.make?.name : product.make,
          model: typeof product.model === 'object' ? product.model?.name : product.model,
          year: product.year,
          body_type: typeof product.body_type === 'object' ? product.body_type?.name : product.body_type,
          position: typeof product.position === 'object' ? product.position?.name : product.position,
          addedAt: new Date().toISOString(),
          slug: product.slug,
          uuid: product.uuid,
          product_uuid: product.product_uuid
        };
        state.items.push(watchlistItem);
      }
      
      saveWatchlistToStorage(state.items);
    },
    
    setWatchlistLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    
    setWatchlistError: (state, action) => {
      state.error = action.payload;
      state.isLoading = false;
    }
  }
});

export const {
  addToWatchlist,
  removeFromWatchlist,
  clearWatchlist,
  toggleWatchlistItem,
  setWatchlistLoading,
  setWatchlistError
} = watchlistSlice.actions;

// Selectors
export const selectWatchlistItems = (state) => state.watchlist.items;
export const selectWatchlistItemCount = (state) => state.watchlist.items.length;
export const selectIsInWatchlist = (state, productId) => 
  state.watchlist.items.some(item => item.id === productId);
export const selectWatchlistLoading = (state) => state.watchlist.isLoading;
export const selectWatchlistError = (state) => state.watchlist.error;

export default watchlistSlice.reducer;
